import { useState } from 'react';
import { Search } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PermissionDenied } from '@/components/ui/permission-denied';
import type { ApiError } from '@/core/api';
import type { AuditLogEntry } from '../api';
import { useAuditLogs } from '../queries';

function diffSummary(entry: AuditLogEntry): string {
  const keys = Object.keys(entry.after ?? {});
  if (!keys.length) return '—';
  return keys
    .map((k) => `${k}: ${JSON.stringify(entry.after[k])}`)
    .join(', ');
}

export function AuditLogPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  const { data, isPending, isError, error, isFetching } = useAuditLogs({
    search: search || undefined,
  });

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput.trim());
  }

  const entries = data?.results ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audit Log</h1>
        <p className="text-muted-foreground text-sm">
          Sensitive changes (org settings, payroll, invoices, roles) — newest first.
        </p>
      </div>

      <form onSubmit={applySearch} className="relative max-w-sm">
        <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
        <Input
          className="pl-8"
          placeholder="Filter by action"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
      </form>

      {isError ? (
        (error as unknown as ApiError)?.status === 403 ? (
          <PermissionDenied message={(error as unknown as ApiError)?.message} />
        ) : (
          <p className="text-destructive text-sm">
            {(error as unknown as ApiError)?.message ?? 'Failed to load the audit log.'}
          </p>
        )
      ) : (
        <div className="rounded-md border" aria-busy={isFetching}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={4}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : entries.length ? (
                entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {new Date(entry.created).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{entry.action}</Badge>
                    </TableCell>
                    <TableCell>{entry.actor_name ?? 'System'}</TableCell>
                    <TableCell className="text-muted-foreground max-w-md truncate text-sm">
                      {diffSummary(entry)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground text-center">
                    No audit entries yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
